import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-labelledby", "children", "className", "defaultValue", "disabled", "fwdRef", "invalid", "name", "onChange", "segmentMinWidth", "size", "value"],
  _excluded2 = ["aria-required", "required"];
import classNames from 'classnames';
import React, { Children, cloneElement, forwardRef, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { FormContext } from '../form/form';
import { FormRowContext } from '../form-row/form-row';
// Add alias for merging labels, since classNames does the job well:
const mergeLabels = classNames;

/**
 * Type guard which checks and guarantees that the object is a type that has props property
 * - Used when SegmentedButtons maps through its children.
 */
function hasProps(object) {
  if (!object) {
    return false;
  }
  if (typeof object === 'string') {
    return false;
  }
  if (typeof object === 'number') {
    return false;
  }
  if (typeof object === 'boolean') {
    return false;
  }
  return 'props' in object;
}

/**
 * _A visually segmented button looking group of options that allows users to make a single selection from._
 *
 * ***
 *
 * The component is basically a different looking group of radio buttons.
 * Contains [`<Option>`](#/Other%20Components/Option) components as its
 * children, though individually changing their size and defining them
 * invalid or disabled is programmatically prevented. Those need to be
 * defined for all the children with this parent component.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<fieldset>` element.
 */

export function SegmentedButton(_ref) {
  let {
      'aria-labelledby': prevLabels,
      children,
      className,
      defaultValue = '',
      disabled: propDisabled,
      fwdRef,
      invalid,
      name: propName,
      onChange,
      segmentMinWidth,
      size: propSize,
      value
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [stateName] = useState(() => "ds-g-" + uuidv4());
  const [inputValue, setInputValue] = useState('');
  const {
    spacing
  } = useContext(FormContext);
  const {
    disabled: rowDisabled,
    id,
    labelId,
    onGroup,
    onGroupItems,
    onHasRadiogroupRole,
    required: contextRequired,
    setAriaInvalidProp
  } = useContext(FormRowContext);
  const size = propSize != null ? propSize : spacing;
  const labels = mergeLabels(prevLabels, labelId);
  const {
      'aria-required': ariaRequired,
      required: propRequired
    } = props,
    restProps = _objectWithoutPropertiesLoose(props, _excluded2);
  // role radiogroup supports adding aria-required to the fieldset
  const required = !labels && (contextRequired || propRequired || ariaRequired && ariaRequired !== 'false');
  function handleChange(event, onOptionChange) {
    setInputValue(event.target.value);
    if (onOptionChange) {
      onOptionChange(event, event.target.value);
    }
    if (onChange) {
      onChange(event, event.target.value);
    }
    if (setAriaInvalidProp) {
      setAriaInvalidProp(undefined); // Reset aria-invalid value
    }
  }
  useEffect(() => {
    if (setAriaInvalidProp && invalid) {
      setAriaInvalidProp(invalid);
    }
  }, [invalid, setAriaInvalidProp]);
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value != null ? value : defaultValue);
    }
  }, [value]);
  useEffect(() => {
    onGroup == null || onGroup();
  }, [onGroup]);
  useEffect(() => {
    onGroupItems == null || onGroupItems();
  }, [onGroupItems]);
  useEffect(() => {
    onHasRadiogroupRole == null || onHasRadiogroupRole();
  }, [onHasRadiogroupRole]);
  return /*#__PURE__*/React.createElement("fieldset", _extends({
    "aria-labelledby": prevLabels ? labels : undefined,
    "aria-required": required ? 'true' : undefined,
    className: buildVersionedClassNames('ds-segmented-button', className, {
      ["ds-segmented-button--" + size]: size !== 'default'
    }),
    id: id,
    ref: fwdRef,
    role: labels ? undefined : 'radiogroup'
  }, restProps, {
    "aria-invalid": labels ? undefined : invalid // aria-invalid is handled in FormRow if inside one
  }), Children.map(children, option => {
    if (hasProps(option)) {
      const {
        className: optionClassName,
        onChange: onOptionChange,
        value: optionValue
      } = option.props;
      const optionProps = {
        checked: optionValue === inputValue,
        className: optionClassName,
        disabled: propDisabled != null ? propDisabled : rowDisabled,
        invalid: invalid,
        // TODO: Clean this up once Option's invalid prop is removed in OPUX 7
        name: propName != null ? propName : stateName,
        onChange: event => handleChange(event, onOptionChange)
      };
      return /*#__PURE__*/React.createElement("div", {
        className: buildVersionedClass('ds-segmented-button__option'),
        style: {
          minWidth: segmentMinWidth
        }
      }, /*#__PURE__*/cloneElement(option, optionProps));
    }
  }));
}
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(SegmentedButton, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'SegmentedButton';
export default Fwd;